(() => {
  'use strict';

  const MAX_RECOMMENDATIONS = 3;
  const MAX_PROMOTED = 1;

  const ADD_ENDPOINT =
    `${window.Shopify?.routes?.root || '/'}cart/add.js`;


  /* =========================================================
     UTILIDADES
     ========================================================= */

  function shuffle(items) {
    const shuffled = [...items];

    for (
      let index = shuffled.length - 1;
      index > 0;
      index -= 1
    ) {
      const randomIndex =
        Math.floor(
          Math.random() * (index + 1)
        );

      [
        shuffled[index],
        shuffled[randomIndex]
      ] = [
        shuffled[randomIndex],
        shuffled[index]
      ];
    }

    return shuffled;
  }


  function getCartFamilies(container) {
    const rawFamilies =
      container.dataset.cartFamilies || '';

    return new Set(
      rawFamilies
        .split('|')
        .map((family) => family.trim())
        .filter(Boolean)
    );
  }


  function uniqueCandidates(candidates) {
    const seen = new Set();

    return candidates.filter((candidate) => {
      const id =
        String(
          candidate.dataset.productId || ''
        );

      if (
        !id ||
        seen.has(id)
      ) {
        return false;
      }

      seen.add(id);

      return true;
    });
  }


  /* =========================================================
     AÑADIR CANDIDATO
     ========================================================= */

  function addCandidate(
    candidate,
    selected,
    selectedIds,
    selectedFamilies
  ) {

    if (
      !candidate ||
      selected.length >= MAX_RECOMMENDATIONS
    ) {
      return false;
    }


    const id =
      String(
        candidate.dataset.productId || ''
      );


    if (
      !id ||
      selectedIds.has(id)
    ) {
      return false;
    }


    selected.push(candidate);

    selectedIds.add(id);


    const family =
      candidate.dataset.family || '';


    if (family) {
      selectedFamilies.add(family);
    }


    return true;
  }


  /* =========================================================
     SELECCIÓN AUTOMÁTICA
     ========================================================= */

  function selectAutomaticTier(
    candidates,
    selected,
    selectedIds,
    selectedFamilies,
    cartFamilies
  ) {

    const available =
      shuffle(
        uniqueCandidates(candidates)
          .filter((candidate) => {

            const id =
              String(
                candidate.dataset.productId || ''
              );

            return (
              id &&
              !selectedIds.has(id)
            );

          })
      );


    const passes = [

      /*
       * Mejor caso:
       * familia no presente en carrito
       * y todavía no utilizada.
       */

      (candidate) => {
        const family =
          candidate.dataset.family || '';

        return (
          family &&
          !cartFamilies.has(family) &&
          !selectedFamilies.has(family)
        );
      },


      /*
       * Familia distinta de las
       * presentes en carrito.
       */

      (candidate) => {
        const family =
          candidate.dataset.family || '';

        return (
          family &&
          !cartFamilies.has(family)
        );
      },


      /*
       * Familia todavía no utilizada
       * en recomendaciones.
       */

      (candidate) => {
        const family =
          candidate.dataset.family || '';

        return (
          family &&
          !selectedFamilies.has(family)
        );
      },


      /*
       * Último recurso dentro
       * de esta capa.
       */

      () => true
    ];


    passes.forEach((test) => {

      available.forEach((candidate) => {

        if (
          selected.length >=
          MAX_RECOMMENDATIONS
        ) {
          return;
        }


        const id =
          String(
            candidate.dataset.productId || ''
          );


        if (
          !id ||
          selectedIds.has(id) ||
          !test(candidate)
        ) {
          return;
        }


        addCandidate(
          candidate,
          selected,
          selectedIds,
          selectedFamilies
        );

      });

    });
  }


  /* =========================================================
     INICIALIZAR RECOMENDACIONES
     ========================================================= */

  function initialiseRecommendations(
    container
  ) {

    if (
      !(container instanceof HTMLElement)
    ) {
      return;
    }


    const recommendations =
      Array.from(
        container.querySelectorAll(
          '[data-mooa-recommendation]'
        )
      );


    recommendations.forEach(
      (recommendation) => {
        recommendation.hidden = true;
      }
    );


    if (
      recommendations.length === 0
    ) {
      container.hidden = true;
      return;
    }


    const cartFamilies =
      getCartFamilies(container);


    const selected = [];

    const selectedIds =
      new Set();

    const selectedFamilies =
      new Set();


    /* =====================================================
       PRIORIDAD 0 — PROMOCIÓN
       ===================================================== */

    const promoted =
      shuffle(
        uniqueCandidates(
          recommendations.filter(
            (recommendation) =>
              Number(
                recommendation.dataset.priority
              ) === 0
          )
        )
      );


    promoted
      .slice(
        0,
        MAX_PROMOTED
      )
      .forEach((candidate) => {

        addCandidate(
          candidate,
          selected,
          selectedIds,
          selectedFamilies
        );

      });


    /* =====================================================
       PRIORIDAD 1 — COMPLEMENTA CON
       ===================================================== */

    const manual =
      shuffle(
        uniqueCandidates(
          recommendations.filter(
            (recommendation) =>
              Number(
                recommendation.dataset.priority
              ) === 1
          )
        )
      );


    manual.forEach((candidate) => {

      if (
        selected.length >=
        MAX_RECOMMENDATIONS
      ) {
        return;
      }


      addCandidate(
        candidate,
        selected,
        selectedIds,
        selectedFamilies
      );

    });


    /* =====================================================
       PRIORIDAD 2 — HISTORIA
       PRIORIDAD 3 — COLECCIÓN
       ===================================================== */

    [2, 3].forEach((priority) => {

      if (
        selected.length >=
        MAX_RECOMMENDATIONS
      ) {
        return;
      }


      selectAutomaticTier(
        recommendations.filter(
          (recommendation) =>
            Number(
              recommendation.dataset.priority
            ) === priority
        ),
        selected,
        selectedIds,
        selectedFamilies,
        cartFamilies
      );

    });


    /* =====================================================
       PRIORIDAD 4 — FALLBACK CATALOGO
       ===================================================== */

    if (
      selected.length <
      MAX_RECOMMENDATIONS
    ) {

      selectAutomaticTier(
        recommendations.filter(
          (recommendation) =>
            Number(
              recommendation.dataset.priority
            ) === 4
        ),
        selected,
        selectedIds,
        selectedFamilies,
        cartFamilies
      );

    }


    /* =====================================================
       MOSTRAR SELECCIÓN
       ===================================================== */

    selected.forEach(
      (recommendation) => {
        recommendation.hidden = false;
      }
    );


    container.hidden =
      selected.length === 0;
  }


  function initialiseAllRecommendations(
    root = document
  ) {

    if (
      root instanceof HTMLElement &&
      root.matches(
        '[data-mooa-cart-recommendations]'
      )
    ) {
      initialiseRecommendations(root);
    }


    root
      .querySelectorAll?.(
        '[data-mooa-cart-recommendations]'
      )
      .forEach(
        initialiseRecommendations
      );
  }


  /* =========================================================
     VARIANTE SELECCIONADA
     ========================================================= */

  function getSelectedVariantId(button) {

    const directVariantId =
      button.dataset.variantId;


    if (directVariantId) {
      return directVariantId;
    }


    const recommendation =
      button.closest(
        '[data-mooa-recommendation]'
      );


    if (!recommendation) {
      return null;
    }


    const selector =
      recommendation.querySelector(
        '[data-variant-selector]'
      );


    return selector?.value || null;
  }


  /* =========================================================
     ESTADO DEL BOTÓN
     ========================================================= */

  function setButtonState(
    button,
    state
  ) {

    if (
      !(button instanceof HTMLButtonElement)
    ) {
      return;
    }


    if (!button.dataset.originalText) {
      button.dataset.originalText =
        button.textContent.trim();
    }


    switch (state) {

      case 'loading':

        button.disabled = true;

        button.setAttribute(
          'aria-busy',
          'true'
        );

        button.textContent =
          'Añadiendo…';

        break;


      case 'success':

        button.disabled = true;

        button.removeAttribute(
          'aria-busy'
        );

        button.textContent =
          '✓ Añadido';

        break;


      case 'error':

        button.disabled = false;

        button.removeAttribute(
          'aria-busy'
        );

        button.textContent =
          'Error';

        break;


      default:

        button.disabled = false;

        button.removeAttribute(
          'aria-busy'
        );

        button.textContent =
          button.dataset.originalText ||
          'Añadir';

    }
  }


  /* =========================================================
     AÑADIR AL CARRITO
     ========================================================= */

  async function addVariantToCart(
    variantId
  ) {

    const response =
      await fetch(
        ADD_ENDPOINT,
        {
          method: 'POST',

          credentials:
            'same-origin',

          headers: {
            Accept:
              'application/json',

            'Content-Type':
              'application/json',

            'X-Requested-With':
              'XMLHttpRequest'
          },

          body:
            JSON.stringify({
              items: [
                {
                  id:
                    Number(
                      variantId
                    ),

                  quantity: 1
                }
              ]
            })
        }
      );


    const data =
      await response.json();


    if (!response.ok) {

      throw new Error(
        data.description ||
        data.message ||
        'No se ha podido añadir el producto.'
      );

    }


    return (
      data.items?.[0] ||
      data
    );
  }


  /* =========================================================
     REFRESCAR CARRITO PRESTIGE

     Prestige expone el evento nativo:
     cart:refresh

     Esto vuelve a renderizar el mini-cart
     con el contenido real de Shopify.
     ========================================================= */

  function refreshPrestigeCart() {

    document.documentElement.dispatchEvent(
      new CustomEvent(
        'cart:refresh',
        {
          bubbles: true
        }
      )
    );

  }


  /* =========================================================
     QUICK ADD
     ========================================================= */

  async function handleQuickAdd(
    button
  ) {

    if (
      !(button instanceof HTMLButtonElement) ||
      button.disabled
    ) {
      return;
    }


    const variantId =
      getSelectedVariantId(button);


    if (!variantId) {

      setButtonState(
        button,
        'error'
      );


      window.setTimeout(
        () => {

          setButtonState(
            button,
            'default'
          );

        },
        1800
      );


      return;
    }


    setButtonState(
      button,
      'loading'
    );


    try {

      await addVariantToCart(
        variantId
      );


      setButtonState(
        button,
        'success'
      );


      /*
       * El producto ya está añadido
       * en Shopify.
       *
       * Ahora pedimos a Prestige que
       * refresque el Cart Drawer.
       */

      refreshPrestigeCart();


      /*
       * El drawer debería ser reemplazado
       * durante el refresco.
       *
       * Si por algún motivo el botón
       * antiguo siguiera en el DOM,
       * restauramos su estado.
       */

      window.setTimeout(
        () => {

          if (
            document.body
              .contains(button)
          ) {

            setButtonState(
              button,
              'default'
            );

          }

        },
        1800
      );


    } catch (error) {

      console.error(
        '[MööA] Error al añadir la recomendación:',
        error
      );


      setButtonState(
        button,
        'error'
      );


      window.setTimeout(
        () => {

          setButtonState(
            button,
            'default'
          );

        },
        2200
      );

    }
  }


  /* =========================================================
     CLICK
     ========================================================= */

  function handleDocumentClick(
    event
  ) {

    const button =
      event.target.closest(
        '.MooaCartRecommendation__Add'
      );


    if (!button) {
      return;
    }


    event.preventDefault();

    event.stopPropagation();


    handleQuickAdd(button);
  }


  /* =========================================================
     START
     ========================================================= */

  function startMooaCartRecommendations() {

    initialiseAllRecommendations();


    document.addEventListener(
      'click',
      handleDocumentClick
    );


    /*
     * Prestige sustituye partes del DOM
     * al refrescar el carrito.
     *
     * Cuando entren los nuevos nodos,
     * volvemos a inicializar nuestras
     * recomendaciones.
     */

    const observer =
      new MutationObserver(
        (mutations) => {

          mutations.forEach(
            (mutation) => {

              mutation.addedNodes
                .forEach((node) => {

                  if (
                    !(node instanceof HTMLElement)
                  ) {
                    return;
                  }


                  initialiseAllRecommendations(
                    node
                  );

                });

            });

        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      startMooaCartRecommendations,
      {
        once: true
      }
    );

  } else {

    startMooaCartRecommendations();

  }

})();